import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate, VIG_REPORT_ROLES as VIG_ALL, VIGILANZA_ROLES } from "../lib/roles";
import { usePerm } from "../context/PermContext";
import { Empty, Field, ImagesInput, Loading, PageHeader, Panel } from "../components/common";
import { VigDigest } from "../components/VigDigest";
import { VigSearch } from "../components/VigSearch";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const VIG_TURNI = ["06.00-14.00", "10.00-18.00", "14.00-22.00", "15.00-22.00", "18.00-24.00", "22.00-06.00", "00.00-08.00"];
const VIG_MEZZI = ["C3 HA260WJ", "Panda HB202GY", "Bike 1", "Bike 2"];
const PORTINERIA = "Portineria";
const TURNI_PORTINERIA = ["22.00-06.00", "06.00-14.00", "14.00-22.00"];
const TURNI_RONDA = ["00.00-08.00", "15.00-22.00", "18.00-24.00"];

const easter = (y) => {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  return new Date(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
};
export const isFestivo = (d) => {
  if (d.getDay() === 0 || d.getDay() === 6) return true;
  const md = `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (["01-01", "01-06", "04-25", "05-01", "06-02", "08-15", "11-01", "12-08", "12-25", "12-26"].includes(md)) return true;
  const em = easter(d.getFullYear()); em.setDate(em.getDate() + 1);
  return em.toDateString() === d.toDateString();
};
const turniRonda = () => (isFestivo(new Date()) ? [...TURNI_RONDA, "10.00-18.00"] : TURNI_RONDA);

const TipoStep = ({ value, onChange }) => (
  <Field label="Per quale turno invii il report?">
    <div className="grid sm:grid-cols-2 gap-3">{[[PORTINERIA, "Turno in portineria"], ["Ronda", "Vigilanza di ronda"]].map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} className={`rounded-2xl border px-4 py-4 text-left font-semibold transition-colors ${value === v ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "bg-white text-[var(--navy)] hover:bg-slate-50"}`} data-testid={`vig-tipo-${v.toLowerCase()}`}>{l}</button>))}
    </div>
  </Field>
);
const VIG_AVVISATO = ["Sala operativa", "Committente", "NUE 112"];

const Pick = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value || ""} onValueChange={onChange}><SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
);

export function VigReportNew() {
  const [sp] = useSearchParams();
  const id = sp.get("id");
  const nav = useNavigate();
  const [f, setF] = useState({ turno: "", mezzo: "", segnalazione: "", provvedimenti: "", avvisato: [], immagini: [] });
  const [loading, setLoading] = useState(false);
  const { viewUser } = usePerm();
  const fiduciario = (viewUser?.mansioni || [viewUser?.mansione]).includes("Vigilanza Fiduciario");
  const [tipo, setTipo] = useState("");
  useEffect(() => {
    if (id) api("/vig-reports/mine").then((d) => { const r = (d.reports || []).find((x) => x.id === id); if (r) { setTipo(r.mezzo === PORTINERIA || r.mezzo === "Sede - Portineria" ? PORTINERIA : "Ronda"); setF({ turno: r.turno || "", mezzo: r.mezzo || "", segnalazione: r.segnalazione || "", provvedimenti: r.provvedimenti || "", avvisato: r.avvisato || [], immagini: r.immagini || [] }); } }).catch(() => {});
  }, [id]);
  const portineria = fiduciario && tipo === PORTINERIA;
  const turni = !fiduciario ? VIG_TURNI : portineria ? TURNI_PORTINERIA : turniRonda();
  const pickTipo = (v) => { setTipo(v); setF({ ...f, turno: "", mezzo: v === PORTINERIA ? PORTINERIA : "" }); };
  const showFields = !fiduciario || (tipo && f.turno);
  const toggle = (a) => setF({ ...f, avvisato: f.avvisato.includes(a) ? f.avvisato.filter((x) => x !== a) : [...f.avvisato, a] });
  const submit = async () => {
    if (fiduciario && !tipo) return toast.error("Scegli se il turno è in portineria o di ronda");
    if (!f.turno) return toast.error("Seleziona il turno");
    if (!portineria && !f.mezzo) return toast.error("Seleziona il mezzo usato");
    if (!f.segnalazione.trim()) return toast.error("Compila la Segnalazione");
    setLoading(true);
    try {
      await api(id ? `/vig-reports/${id}` : "/vig-reports", { method: id ? "PUT" : "POST", body: portineria ? { ...f, mezzo: PORTINERIA } : f });
      toast.success(id ? "Report aggiornato" : "Report inviato"); nav("/vigilanza/mine");
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-2xl">
      <PageHeader subtitle="Vigilanza" title={id ? "Modifica report" : "Report turno Vigilanza"} />
      <Panel className="space-y-5">
        {fiduciario && <TipoStep value={tipo} onChange={pickTipo} />}
        {(!fiduciario || tipo) && <div className="grid sm:grid-cols-2 gap-4">
          <Field label={fiduciario ? "Orario turno" : "Turno"}><Pick value={f.turno} onChange={(v) => setF({ ...f, turno: v })} options={turni.includes(f.turno) || !f.turno ? turni : [...turni, f.turno]} placeholder="Seleziona il turno..." testid="vig-turno-select" /></Field>
          {showFields && !portineria && <Field label="Mezzo usato"><Pick value={f.mezzo} onChange={(v) => setF({ ...f, mezzo: v })} options={VIG_MEZZI} placeholder="Seleziona il mezzo..." testid="vig-mezzo-select" /></Field>}
        </div>}
        {showFields && <>
        <Field label="Segnalazione"><Textarea rows={5} value={f.segnalazione} onChange={(e) => setF({ ...f, segnalazione: e.target.value })} placeholder="Descrivi la segnalazione..." data-testid="vig-segnalazione-input" /></Field>
        <Field label="Provvedimenti adottati"><Textarea rows={3} value={f.provvedimenti} onChange={(e) => setF({ ...f, provvedimenti: e.target.value })} placeholder="Descrivi i provvedimenti..." data-testid="vig-provvedimenti-input" /></Field>
        <Field label="Avvisato"><div className="flex flex-wrap gap-4 pt-1">{VIG_AVVISATO.map((a) => <label key={a} className="flex items-center gap-2 text-sm"><Checkbox checked={f.avvisato.includes(a)} onCheckedChange={() => toggle(a)} data-testid={`vig-avvisato-${a.replace(/\s/g, "-")}`} />{a}</label>)}</div></Field>
        <Field label="Immagini (max 3, compresse automaticamente)"><ImagesInput value={f.immagini} onChange={(v) => setF({ ...f, immagini: v })} max={3} testid="vig-images" /></Field>
        <Button onClick={submit} disabled={loading} className="rounded-full bg-[var(--teal)] px-8" data-testid="vig-submit-button">{id ? "Salva modifiche" : "Invia Report"}</Button>
        </>}
      </Panel>
    </div>
  );
}

const imgsOf = (r) => { let l = r.immagini; if (typeof l === "string") { try { l = JSON.parse(l); } catch { l = []; } } return Array.isArray(l) ? l : []; };
const monthOf = (d) => (d || "").slice(0, 7);
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const Row = ({ r, testid }) => (
  <Link to={`/vigilanza/report/${r.id}?m=${monthOf(r.data)}`} className="grid grid-cols-2 sm:grid-cols-[110px_160px_120px_1fr_auto] gap-x-4 gap-y-1 items-center px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={testid}>
    <span className="font-semibold text-[var(--navy)]">{fmtDate(r.data)}</span>
    <span className="text-sm text-slate-600" data-testid={`${testid}-servizio`}>{r.servizio || r.profilo || "—"}</span>
    <span><span className="tag tag-teal">{r.turno}</span></span>
    <span className="text-sm text-[var(--navy)]">{r.author_name}</span>
    <span className="flex items-center gap-1 text-sm font-semibold text-[var(--teal)]"><Eye className="w-4 h-4" />Dettaglio</span>
  </Link>
);

const Head = () => (
  <div className="hidden sm:grid grid-cols-[110px_160px_120px_1fr_auto] gap-x-4 px-4 py-2 text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
    <span>Data</span><span>Servizio</span><span>Turno</span><span>Dipendente</span><span>Messaggio</span>
  </div>
);

const SERVIZI = ["Tutti", "Fiduciario Portineria", "Fiduciario Ronda", "GPG"];
const ServiziFilter = ({ list, value, onChange }) => (
  <div className="flex flex-wrap gap-2 mb-4" data-testid="vig-servizio-filter">
    {SERVIZI.map((s) => { const n = s === "Tutti" ? list.length : list.filter((r) => r.servizio === s).length; return (
      <button key={s} onClick={() => onChange(s)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${value === s ? "bg-[var(--navy)] text-white" : "bg-white text-[var(--navy)] ring-1 ring-slate-200 hover:bg-slate-50"}`} data-testid={`vig-servizio-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s} ({n})</button>); })}
  </div>
);

export function VigReportsList() {
  const now = new Date();
  const [srv, setSrv] = useState("Tutti");
  const [ym, setYm] = useState({ m: now.getMonth() + 1, y: now.getFullYear() });
  const [list, setList] = useState(null);
  const [found, setFound] = useState(null);
  useEffect(() => {
    let live = true;
    setList(null);
    api(`/web/vig-reports?mese=${ym.m}&anno=${ym.y}`).then((d) => { if (live) setList(d.reports || []); }).catch((e) => { if (live) { toast.error(e.message); setList([]); } });
    return () => { live = false; };
  }, [ym]);
  const shift = (n) => setYm(({ m, y }) => { const d = new Date(y, m - 1 + n, 1); return { m: d.getMonth() + 1, y: d.getFullYear() }; });
  return (
    <div>
      <VigDigest />
      <VigSearch onResults={setFound} />
      {found ? (found.reports.length === 0 ? <Empty title="Nessun report trovato" text="Prova con un'altra parola o un altro filtro." /> : (
        <div data-testid="vig-search-results">
          <p className="text-sm text-slate-600 mb-3" data-testid="vig-search-count">{found.total} report trovati{found.total > found.reports.length ? `, mostrati i ${found.reports.length} più recenti` : ""}</p>
          <Panel className="!p-0 overflow-hidden"><Head /><div className="divide-y">{found.reports.map((r) => <Row key={r.id} r={r} testid={`vig-search-row-${r.id}`} />)}</div></Panel>
        </div>)) : <>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => shift(-1)} className="p-2 rounded-full hover:bg-white" data-testid="vig-reports-prev"><ChevronLeft className="w-5 h-5" /></button>
        <span className="font-semibold text-[var(--navy)] min-w-[140px] text-center" data-testid="vig-reports-month">{MESI[ym.m - 1]} {ym.y}</span>
        <button onClick={() => shift(1)} className="p-2 rounded-full hover:bg-white" data-testid="vig-reports-next"><ChevronRight className="w-5 h-5" /></button>
      </div>
      {!list ? <Loading /> : list.length === 0 ? <Empty title="Nessun report Vigilanza" text="Nel mese scelto non sono stati inseriti report." /> : (<>
        <ServiziFilter list={list} value={srv} onChange={setSrv} />
        <Panel className="!p-0 overflow-hidden"><Head /><div className="divide-y" data-testid="vig-reports-list">{list.filter((r) => srv === "Tutti" || r.servizio === srv).map((r) => <Row key={r.id} r={r} testid={`vig-reports-row-${r.id}`} />)}</div></Panel></>)}
      </>}
    </div>
  );
}

export function VigReports() {
  return <div><PageHeader subtitle="Vigilanza" title="Report Vigilanza" /><VigReportsList /></div>;
}

export function VigMineList() {
  const [list, setList] = useState(null);
  const { viewUser } = usePerm();
  useEffect(() => {
    const profilo = (viewUser?.mansioni || [viewUser?.mansione]).filter((m) => VIG_ALL.includes(m)).join(", ");
    const name = `${viewUser?.nome || ""} ${viewUser?.cognome || ""}`.trim();
    api("/vig-reports/mine").then((d) => setList((d.reports || []).map((r) => ({ ...r, profilo, author_name: name })))).catch(() => setList([]));
  }, [viewUser]);
  if (!list) return <Loading />;
  return list.length === 0 ? <Empty title="Nessun report inviato" text="I report che invii compariranno qui." /> : (
    <Panel className="!p-0 overflow-hidden"><Head /><div className="divide-y" data-testid="vig-mine-list">{list.map((r) => <Row key={r.id} r={r} testid={`vig-report-${r.id}`} />)}</div></Panel>);
}

export function VigReportDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const [y, m] = (sp.get("m") || "").split("-").map(Number);
    const now = new Date();
    api(`/web/vig-reports/${id}?mese=${m || now.getMonth() + 1}&anno=${y || now.getFullYear()}`).then(setD).catch((e) => setErr(e.message));
  }, [id, sp]);
  if (err) return <Empty title="Report non disponibile" text={err} />;
  if (!d) return <Loading />;
  const r = d.report;
  return (
    <div className="max-w-3xl">
      <PageHeader subtitle="Report Vigilanza" title={`Report del ${fmtDate(r.data)}`} actions={d.mine && <Button asChild variant="outline" className="rounded-full" data-testid="vig-detail-edit"><Link to={`/vigilanza/new?id=${r.id}`}><Pencil className="w-4 h-4 mr-1" />Modifica</Link></Button>} />
      <Panel className="space-y-4" data-testid="vig-report-detail">
        <div className="flex flex-wrap gap-2 items-center"><span className="tag tag-teal">{r.turno}</span>{(r.servizio || r.profilo) && <span className="tag" data-testid="vig-detail-servizio">{r.servizio || r.profilo}</span>}{r.updated_at && <span className="tag tag-amber">Modificato</span>}{r.source && <span className="tag" data-testid="vig-detail-imported">Archivio Vigilpol</span>}</div>
        <p className="text-sm text-slate-600"><b>Dipendente:</b> {r.author_name}{r.source && !r.author_id && <span className="text-slate-400"> (non associato a un utente)</span>}</p>
        {r.postazione && <p className="text-sm text-slate-600" data-testid="vig-detail-postazione"><b>Postazione:</b> {r.postazione}</p>}
        {r.colleghi && <p className="text-sm text-slate-600"><b>In servizio con:</b> {r.colleghi}</p>}
        {r.evento && <p className="text-sm text-slate-600"><b>Data / ora evento:</b> {r.evento}</p>}
        {r.mezzo === PORTINERIA || r.mezzo === "Sede - Portineria" ? <p className="text-sm text-slate-600" data-testid="vig-detail-portineria"><b>Tipo turno:</b> Turno in portineria</p> : <p className="text-sm text-slate-600"><b>Mezzo usato:</b> {r.mezzo}</p>}
        <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Segnalazione</p><p className="text-[var(--navy)] whitespace-pre-wrap" data-testid="vig-detail-segnalazione">{r.segnalazione}</p></div>
        {r.provvedimenti && <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Provvedimenti adottati</p><p className="text-[var(--navy)] whitespace-pre-wrap">{r.provvedimenti}</p></div>}
        {(r.avvisato || []).length > 0 && <p className="text-sm text-slate-600"><b>Avvisato:</b> {r.avvisato.join(", ")}</p>}
        {imgsOf(r).length > 0 && <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Foto allegate</p><div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vig-detail-photos">{imgsOf(r).map((src, i) => (
          <a key={i} href={src} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); const w = window.open(); if (w) w.document.write(`<img src="${src}" style="max-width:100%">`); }}><img src={src} alt={`Foto ${i + 1}`} className="w-full aspect-square object-cover rounded-xl border" data-testid={`vig-detail-photo-${i}`} /></a>))}</div></div>}
        {(r.allegati || []).length > 0 && <div data-testid="vig-detail-allegati"><p className="text-xs uppercase tracking-wider text-slate-500 mb-1">File allegati (sito Vigilpol)</p>{r.allegati.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer" className="block text-sm text-[var(--teal)] underline truncate">{decodeURIComponent(u.split("/").pop())}</a>)}</div>}
        <p className="text-xs text-slate-400">Inserito il {fmtDate(r.created_at, true)}</p>
      </Panel>
    </div>
  );
}

export function VigMine() {
  return (
    <div>
      <PageHeader subtitle="Vigilanza" title="I miei report" actions={<Button asChild className="rounded-full bg-[var(--teal)]" data-testid="vig-new-link"><Link to="/vigilanza/new">Nuovo report</Link></Button>} />
      <VigMineList />
    </div>
  );
}

const DEST = [["tutti", "Tutto il personale"], ...VIGILANZA_ROLES.map((m) => [m, m])];

export function ComunicaVigilanza() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ titolo: "", messaggio: "", destinatari: "tutti" });
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("/web/vig-comms").then((d) => setList(d.communications || [])).catch((e) => { toast.error(e.message); setList([]); }), []);
  useEffect(() => { load(); }, [load]);
  const reset = () => { setEditId(null); setF({ titolo: "", messaggio: "", destinatari: "tutti" }); };
  const send = async () => {
    if (!f.titolo.trim() || !f.messaggio.trim()) return toast.error("Compila tutti i campi");
    setBusy(true);
    try {
      await api(editId ? `/web/vig-comms/${editId}` : "/web/vig-comms", { method: editId ? "PUT" : "POST", body: f });
      toast.success(editId ? "Comunicazione aggiornata" : "Comunicazione inviata"); reset(); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const del = async (c) => { if (!window.confirm("Vuoi eliminare questa comunicazione?")) return; try { await api(`/web/vig-comms/${c.id}`, { method: "DELETE" }); toast.success("Comunicazione eliminata"); load(); } catch (e) { toast.error(e.message); } };
  return (
    <div>
      <PageHeader subtitle="Responsabile vigilanza" title="Comunicazioni Vigilanza" />
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
        <Panel className="space-y-4 h-fit">
          <h2 className="font-display font-bold text-[var(--navy)]">{editId ? "Modifica comunicazione" : "Nuova comunicazione"}</h2>
          {!editId && <Field label="Destinatari"><div className="flex flex-wrap gap-2">{DEST.map(([v, l]) => <button key={v} onClick={() => setF({ ...f, destinatari: v })} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.destinatari === v ? "bg-[var(--navy)] text-white" : "bg-white"}`} data-testid={`vigcomm-role-${v.replace(/\s/g, "-")}`}>{l}</button>)}</div></Field>}
          <Field label="Titolo"><Input value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} data-testid="vigcomm-title-input" /></Field>
          <Field label="Messaggio"><Textarea rows={5} value={f.messaggio} onChange={(e) => setF({ ...f, messaggio: e.target.value })} data-testid="vigcomm-message-input" /></Field>
          <div className="flex gap-2"><Button onClick={send} disabled={busy} className="rounded-full bg-[var(--teal)] flex-1" data-testid="vigcomm-send-button">{editId ? "Salva modifiche" : "Invia Comunicazione"}</Button>{editId && <Button variant="ghost" onClick={reset} data-testid="vigcomm-cancel-edit">Annulla modifica</Button>}</div>
        </Panel>
        <div>{!list ? <Loading /> : list.length === 0 ? <Empty title="Nessuna comunicazione" text="Qui trovi le comunicazioni inviate al personale di Vigilanza." /> : <div className="space-y-3" data-testid="vigcomm-list">{list.map((c) => (
          <Panel key={c.id} className="!p-5" data-testid={`vigcomm-item-${c.id}`}><div className="flex justify-between gap-2"><div className="flex flex-wrap gap-2 items-center"><span className="font-semibold text-[var(--navy)]">{c.titolo}</span><span className="tag tag-teal">{c.destinatari}</span></div>
            <div className="flex gap-1"><button onClick={() => { setEditId(c.id); setF({ titolo: c.titolo, messaggio: c.messaggio, destinatari: "tutti" }); }} className="text-slate-400 hover:text-[var(--navy)]" data-testid={`vigcomm-edit-${c.id}`}><Pencil className="w-4 h-4" /></button><button onClick={() => del(c)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`vigcomm-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button></div></div>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{c.messaggio}</p><p className="text-xs text-slate-400 mt-2">{c.author_name} · {fmtDate(c.created_at, true)}</p></Panel>))}</div>}</div>
      </div>
    </div>
  );
}
